import { privatePageTitles, seoByPath, shopSeo, treatmentsSeo, SITE_NAME, type SeoEntry } from '../../data/seo'
import { founder } from '../../data/content'
import { pages } from '../../data/pages'
import { effectiveSeo } from '../../core/rules/seo'
import { articleSeoAuto, productSeoAuto, seoPagePaths } from '../../utils/seoAuto'
import type { SeoPageKey } from '../../core/types/pages'
import { legalByPath } from '../../data/legal'
import { shopFaq, salonFaq } from '../../data/faq'
import type { CatalogIndex } from '../../utils/catalog'
import { absoluteUrl, safeDecode } from './seoText'
import { articleSchema, breadcrumbSchema, faqSchema, productSchema } from './schemas'

export type ResolvedSeo = {
  entry: SeoEntry
  schema: object | object[] | null
  /** Veelgestelde vragen op deze pagina, of `null`. */
  faq: object | null
  type: 'website' | 'article' | 'product'
  /** Eigen deelafbeelding (product/artikel); anders de algemene OG_IMAGE. */
  image?: string
  notFound?: boolean
}

const PRODUCT_PATH = /^\/webshop\/(.+)$/
const ARTICLE_PATH = /^\/journal\/(.+)$/

/** Pagina's met een FAQ-blok en welke vragen daar staan. */
function faqFor(pathname: string) {
  if (pathname === '/webshop' || pathname === '/routines') return faqSchema(shopFaq)
  if (pathname === '/behandelingen') return faqSchema(salonFaq)
  return null
}

/** Vaste pagina → eigen Google-tekst uit Paginateksten (pages.seo), anders de standaard. */
const pageKeyByPath = Object.fromEntries(Object.entries(seoPagePaths).map(([k, p]) => [p, k])) as Record<string, SeoPageKey>

function withPageSeo(pathname: string, entry: SeoEntry): { entry: SeoEntry; image?: string } {
  const key = pageKeyByPath[pathname]
  const own = key ? pages.seo?.[key] : undefined
  if (!own) return { entry }
  const r = effectiveSeo(own, { title: entry.title, description: entry.description, image: '' })
  return { entry: { ...entry, title: r.title, description: r.description }, image: r.image ? absoluteUrl(r.image) : undefined }
}

/** SEO en structured data voor een pad — product, artikel, juridisch of vaste pagina. Puur. */
export function resolveSeo(pathname: string, url: string, catalog: CatalogIndex): ResolvedSeo {
  const base = { schema: null, faq: faqFor(pathname), type: 'website' as const }
  const { productById, articleBySlug, brandNames } = catalog

  if (pathname === '/webshop') {
    return {
      ...base,
      entry: shopSeo(brandNames),
      schema: breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Webshop', path: '/webshop' },
      ]),
    }
  }
  if (pathname === '/behandelingen') {
    return { ...base, ...withPageSeo(pathname, treatmentsSeo(catalog.treatments.filter((t) => t.signature).map((t) => t.name))) }
  }

  const productId = pathname.match(PRODUCT_PATH)?.[1]
  const found = productId ? productById(safeDecode(productId)) : undefined
  // Cadeaubonnen hebben geen productpagina (die rendert een 404) — dus ook geen Product-schema.
  const product = found?.kind === 'giftcard' || found?.kind === 'kuur' ? undefined : found
  if (product) {
    const seo = effectiveSeo(product.seo, productSeoAuto(product))
    return {
      ...base,
      type: 'product',
      image: absoluteUrl(seo.image),
      entry: {
        title: seo.title,
        description: seo.description,
        keywords: [product.brand, product.name, ...product.skinTypes, 'vegan skincare'],
      },
      schema: [
        productSchema(product, url, catalog.reviewsFor(product.id)),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Webshop', path: '/webshop' },
          { name: product.name, path: pathname },
        ]),
      ],
    }
  }

  const slug = pathname.match(ARTICLE_PATH)?.[1]
  const article = slug ? articleBySlug(safeDecode(slug)) : undefined
  if (article) {
    const seo = effectiveSeo(article.seo, articleSeoAuto(article))
    return {
      ...base,
      type: 'article',
      image: article.image || article.seo?.image ? absoluteUrl(seo.image) : undefined,
      entry: {
        title: seo.title,
        description: seo.description,
        keywords: [article.category, 'skincare tips', founder.name],
      },
      schema: articleSchema(article, url),
    }
  }

  const legal = legalByPath(pathname)
  if (legal) return { ...base, entry: { title: legal.seoTitle, description: legal.description, keywords: [] } }

  const known = seoByPath[pathname]
  if (known) {
    // De protocollen horen onder de webshop: zo toont Google het pad in de zoekresultaten.
    const crumbs =
      pathname === '/routines'
        ? breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Webshop', path: '/webshop' },
            { name: 'Protocollen', path: '/routines' },
          ])
        : null
    return { ...base, ...withPageSeo(pathname, known), ...(crumbs ? { schema: crumbs } : {}) }
  }

  // Afgeschermde pagina's (afrekenen, bestelling, account, nieuwsbrief): eigen tabtitel, en
  // noindex via isNoIndexPath() in Seo.tsx. Geen "niet gevonden": de pagina bestaat wel.
  const privatePrefix = Object.keys(privatePageTitles).find((p) => pathname === p || pathname.startsWith(`${p}/`))
  if (privatePrefix) {
    return {
      ...base,
      faq: null,
      entry: { title: `${privatePageTitles[privatePrefix]} | ${SITE_NAME}`, description: seoByPath['/']?.description ?? '', keywords: [] },
    }
  }

  // Onbekend pad (404, verwijderd product of artikel): nooit indexeren met de homepage-tekst,
  // anders ziet Google het als "soft 404" of dubbele content.
  return {
    ...base,
    faq: null,
    entry: { title: `Pagina niet gevonden | ${SITE_NAME}`, description: seoByPath['/']?.description ?? '', keywords: [] },
    notFound: true,
  }
}
